import { RefObject, useCallback, useEffect, useRef } from 'react'
import { APPSTATE, SNAPVIDEOSTATE } from '../../state'
import { setPinHelperID } from '../../state/internal/rooms'
import { isTouchDevice } from '../../utilities/browser'
import { COMPONENT_WIDTHS } from '../theme'

export const showDragHelper = (id: number) => {
    if (APPSTATE.app.isMobile || isTouchDevice) return
    setPinHelperID(id)
    const pinDragHelper: HTMLDivElement =
        document.querySelector('#pin-drag-helper')
    if (pinDragHelper) {
        pinDragHelper.style.marginRight = '0px'
        pinDragHelper.style.opacity = '1'
    }
}

const getDistance = (mouseX: number, mouseY: number, elem: HTMLElement) => {
    const { top, left, bottom, right } = elem.getBoundingClientRect()
    const centerY = top + (bottom - top) / 2
    const centerX = left + (right - left) / 2
    const distance = Math.hypot(centerX - mouseX, centerY - mouseY)

    return distance
}

export const highlightDragHelper = (mouseX: number, mouseY: number) => {
    if (isTouchDevice) return // PinDragHelper only rendered on non-touch devices

    // targets are individually flag-gated (PinDragHelper) — highlight
    // whichever subset is actually in the DOM instead of requiring all three
    const targets = [
        { id: '#pin-drag-helper-pin', maxDistance: 350 },
        { id: '#pin-drag-helper-friend', maxDistance: 500 },
        { id: '#pin-drag-helper-bookmark', maxDistance: 350 },
    ]
        .map(({ id, maxDistance }) => ({
            element: document.querySelector<HTMLDivElement>(id),
            maxDistance,
        }))
        .filter(({ element }) => element)

    // find the distances we are to the icons and scale/color the closest icon
    let closest: { element: HTMLDivElement; maxDistance: number } = null
    let closestDistance = Infinity
    for (const target of targets) {
        const distance = getDistance(mouseX, mouseY, target.element)
        if (distance < closestDistance) {
            closestDistance = distance
            closest = target
        }
    }
    for (const { element, maxDistance } of targets) {
        if (element === closest?.element && closestDistance < maxDistance) {
            const scalar = 1 + Math.min(1 / (closestDistance / 35), 0.75)
            element.style.scale = scalar + ''
        } else {
            element.style.scale = '1'
        }
    }
}

export const hideDragHelper = () => {
    // TODO: this should probably be stored in state and have the styling reflect the state instead of styling directly here?
    if (isTouchDevice) return // PinDragHelper only rendered on non-touch devices
    // setPinHelperID()
    const pinDragHelper: HTMLDivElement =
        document.querySelector('#pin-drag-helper')
    if (pinDragHelper) {
        pinDragHelper.style.marginRight = `-${
            COMPONENT_WIDTHS.dragSidebar + 10
        }px`
        pinDragHelper.style.opacity = '0'
    }
}

export type SnapSize = {
    width: number
    height: number
    className: string
}

export const useDragHelper = (
    id: number,
    snapRef?: RefObject<HTMLImageElement>,
) => {
    const dragMoveThrottle = useRef<number>(null)
    const dragImageRef = useRef<HTMLImageElement>(null)

    const removeDragImage = useCallback(() => {
        dragImageRef.current?.remove()
        dragImageRef.current = null
    }, [])

    const dragStart = useCallback(
        (event) => {
            if (isTouchDevice) return // PinDragHelper only rendered on non-touch devices
            event.dataTransfer.effectAllowed = 'move'

            // Handle snap preview if provided (for components that need it)
            if (snapRef?.current && SNAPVIDEOSTATE[id]?.playing) {
                removeDragImage()
                const dragImage = snapRef.current.cloneNode(
                    true,
                ) as HTMLImageElement
                dragImage.width = 128
                dragImage.height = 72
                dragImage.className = ''
                dragImage.style.position = 'fixed'
                dragImage.style.top = '-10000px'
                dragImage.style.left = '-10000px'
                dragImage.style.pointerEvents = 'none'
                document.body.appendChild(dragImage)
                dragImageRef.current = dragImage
                event.dataTransfer.setDragImage(dragImage, 64, 36)
            }

            event.dataTransfer.setData('text/plain', id)
            showDragHelper(id)
        },
        [id, removeDragImage, snapRef],
    )

    const dragMove = useCallback((event) => {
        if (isTouchDevice) return // PinDragHelper only rendered on non-touch devices
        const { clientX, clientY } = event

        if (dragMoveThrottle.current) {
            cancelAnimationFrame(dragMoveThrottle.current)
        }

        dragMoveThrottle.current = requestAnimationFrame(() => {
            highlightDragHelper(clientX, clientY)
        })
    }, [])

    const dragEnd = useCallback(() => {
        if (isTouchDevice) return // PinDragHelper only rendered on non-touch devices

        if (dragMoveThrottle.current) {
            cancelAnimationFrame(dragMoveThrottle.current)
            dragMoveThrottle.current = null
        }

        removeDragImage()
        hideDragHelper()
    }, [removeDragImage])

    useEffect(() => {
        return () => {
            removeDragImage()
        }
    }, [removeDragImage])

    return {
        dragStart,
        dragMove,
        dragEnd,
    }
}
